import Graylog from 'components/domain/analytics/Graylog/Graylog';
import { CalendarView } from 'components/domain/calendar/types/CalendarTypes';
import { ISchedulingFilterData } from 'containers/scheduling/SchedulingFilter/SchedulingFilter';
import { useActiveMember } from 'hooks';
import { useCallback, useMemo } from 'react';
import AnalyticsEvent from 'utils/constants/analytics/analyticsEvent';
import { SchedulingCalendarActionTypes } from 'utils/constants/graylogActionTypes';

function useSchedulingAnalytics() {
  const member = useActiveMember();

  const recordViewViewed = useCallback(
    (view: CalendarView) => {
      Graylog({
        shortMsg: AnalyticsEvent.scheduling.SCHEDULING_CALENDAR_VIEW_VIEWED,
        logObject: {
          _member_id: member.id,
          _member_position_id: member.positionId,
          _action_type: view,
        },
      });
    },
    [member.id, member.positionId]
  );

  const recordRangeSelectionActions = useCallback(
    (view: CalendarView, action: keyof typeof SchedulingCalendarActionTypes['rangeSelectionAction']) => {
      Graylog({
        shortMsg: AnalyticsEvent.scheduling.SCHEDULING_CALENDAR_RANGE_SELECTION_ACTIONS,
        logObject: {
          _member_id: member.id,
          _member_position_id: member.positionId,
          _view: view,
          _action_type: SchedulingCalendarActionTypes.rangeSelectionAction[action],
        },
      });
    },
    [member.id, member.positionId]
  );

  const recordPopoverAction = useCallback(
    (view: CalendarView, action: keyof typeof SchedulingCalendarActionTypes['popoverAction']) => {
      Graylog({
        shortMsg: AnalyticsEvent.scheduling.SCHEDULING_CALENDAR_POPOVER_ACTIONS,
        logObject: {
          _member_id: member.id,
          _member_position_id: member.positionId,
          _view: view,
          _action_type: SchedulingCalendarActionTypes.popoverAction[action],
        },
      });
    },
    [member.id, member.positionId]
  );

  const recordSchedulingCalendarEvents = useCallback(
    (view: CalendarView, action: keyof typeof SchedulingCalendarActionTypes['event']) => {
      Graylog({
        shortMsg: AnalyticsEvent.scheduling.SCHEDULING_CALENDAR_EVENTS,
        logObject: {
          _member_id: member.id,
          _member_position_id: member.positionId,
          _view: view,
          _action_type: SchedulingCalendarActionTypes.event[action],
        },
      });
    },
    [member.id, member.positionId]
  );

  const recordFilterSet = useCallback(
    (view: CalendarView, filterData: ISchedulingFilterData) => {
      Graylog({
        shortMsg: AnalyticsEvent.scheduling.SCHEDULING_CALENDAR_FILTER_SET,
        logObject: {
          _member_id: member.id,
          _member_position_id: member.positionId,
          _view: view,
          _filter_member_id: filterData.employeeId,
          _filter_member_group_id: filterData.employeeGroupId,
          _filter_position_id: filterData.positionId,
          _filter_color: filterData.colors,
          _filter_project_id: filterData.projectId,
          _filter_project_group_id: filterData.projectGroupId,
          _filter_cost_code_id: filterData.costCodeId,
          _filter_cost_code_group_id: filterData.costCodeGroupId,
          _filter_equipment_id: filterData.equipmentId,
          _filter_equipment_category_id: filterData.equipmentCategoryId,
          _filter_type: filterData.type,
        },
      });
    },
    [member.id, member.positionId]
  );

  return useMemo(
    () => ({
      recordViewViewed,
      recordRangeSelectionActions,
      recordPopoverAction,
      recordSchedulingCalendarEvents,
      recordFilterSet,
    }),
    [
      recordFilterSet,
      recordPopoverAction,
      recordRangeSelectionActions,
      recordSchedulingCalendarEvents,
      recordViewViewed,
    ]
  );
}

export default useSchedulingAnalytics;
